/*
 * Bright skin check – routine finder
 *
 * PRIVACY BY DESIGN
 * - The photo is analysed only in this browser. This file contains no code that
 *   uploads, sends or stores an image.
 * - The camera is switched off right after the photo is taken; the image data is
 *   wiped as soon as the analysis is done (a few hundred milliseconds).
 * - Answers and results are kept only in page memory. Nothing is written to
 *   cookies, localStorage or the URL.
 */

/* ------------------------------------------------------------------ */
/* Texts                                                               */
/* ------------------------------------------------------------------ */

export const I18N = {
  en: {
    eyebrow: 'Bright skin check',
    start_title: 'Find your Bright routine',
    start_lead: 'Takes about 2 minutes. One selfie and five short questions — then your routine, made for your skin.',
    step1_t: 'Take a selfie',
    step1_d: 'We look at visible shine, redness and evenness of tone.',
    step2_t: 'Answer 5 short questions',
    step2_d: 'How your skin feels and what you would like to change.',
    step3_t: 'Get your routine',
    step3_d: 'Morning and evening, step by step, with Bright products.',
    start_cta: 'Start skin check',
    skip_photo: 'Skip the photo — questions only',
    photo_stays: 'Your photo never leaves your device',
    desktop_hint: 'Tip: phone cameras give the best result. Open this page on your phone, or use your webcam.',
    progress: 'Step {n} of 4 · {label}',
    p_privacy: 'Privacy',
    p_ready: 'Get ready',
    p_photo: 'Your photo',
    p_questions: 'Questions',
    privacy_title: 'Before we start',
    privacy_lead: 'Your privacy matters to us. Here is what happens with your photo.',
    privacy_1: 'Your photo is analysed on your device. It is never uploaded or stored.',
    privacy_2: 'No account needed. Closing the page deletes everything.',
    privacy_3: 'This is a cosmetic skin check, not a medical diagnosis.',
    consent: 'I am at least 16 years old and agree that my photo is analysed on this device for my skin check.',
    privacy_link: 'Privacy policy',
    continue: 'Continue',
    back: 'Back',
    tips_title: 'For the best result',
    tips_lead: 'A good photo makes your routine more accurate.',
    tip1_t: 'Good daylight',
    tip1_d: 'Face a window. Avoid strong shadows and backlight.',
    tip2_t: 'Clean skin',
    tip2_d: 'No makeup, so we can see your skin as it is.',
    tip3_t: 'Free face',
    tip3_d: 'Hair back, glasses off.',
    tip4_t: 'Look straight',
    tip4_d: 'Neutral expression, camera at eye level.',
    open_camera: 'Open camera',
    camera_note: 'Your browser will ask for camera access.',
    preparing: 'Preparing the skin check…',
    camera_place: 'Place your face inside the oval',
    chip_light: 'Good light',
    chip_centre: 'Face centred',
    chip_still: 'Hold still',
    chip_noface: 'Looking for your face…',
    take_photo: 'Take photo',
    close: 'Close',
    camera_footer: 'Analysed on your device · never uploaded',
    analysing: 'Analysing your skin…',
    camera_error: 'We could not start the camera. You can continue with the questions only.',
    engine_error: 'The photo analysis is not available right now. You can continue with the questions only.',
    noface_error: 'We could not find a face in the photo. Please try again in good light.',
    try_again: 'Try again',
    questions_only: 'Continue with questions only',
    seen_title: 'What your skin check noticed',
    seen_lead: 'Based on what the camera can see. Your answers decide the routine.',
    seen_shine: 'Shine',
    seen_redness: 'Redness',
    seen_tone: 'Evenness of tone',
    lvl_shine_0: 'Low',
    lvl_shine_1: 'Visible in the T-zone',
    lvl_shine_2: 'Visible all over',
    lvl_red_0: 'Low',
    lvl_red_1: 'Some',
    lvl_red_2: 'Noticeable',
    lvl_tone_0: 'Even',
    lvl_tone_1: 'Slightly uneven',
    lvl_tone_2: 'Uneven',
    seen_light: 'The light was not ideal, so please check the pre-selected answers carefully.',
    seen_cta: 'Continue to the questions',
    from_scan: 'From your skin check',
    q_counter: 'Question {n} of 5',
    choose_up_to_2: 'Choose up to 2',
    q1: 'Right after washing your face, before applying anything, how does your skin feel?',
    q1_dry: 'Tight, sometimes flaky',
    q1_normal: 'Comfortable',
    q1_combination: 'Comfortable, but shiny on forehead, nose and chin within a few hours',
    q1_oily: 'Shiny all over within a few hours',
    q2: 'How does your skin react to new products, sun or cold?',
    q2_calm: 'Calm, it rarely reacts',
    q2_some: 'Sometimes a little red',
    q2_often: 'Often red, stinging or itchy',
    q3: 'What would you love to see in your mirror in four weeks?',
    q3_firm: 'Firmer, more elastic skin',
    q3_tone: 'A more even skin tone',
    q3_clear: 'Clearer skin, fewer blemishes',
    q3_calm: 'Calmer skin, less redness',
    q3_hydrate: 'Plump, hydrated, glowing skin',
    q3_healthy: 'Simply healthy, balanced skin',
    q4: 'How do your pores and blemishes look on most days?',
    q4_barely: 'Barely visible',
    q4_visible: 'Pores visible on nose and cheeks',
    q4_occasional: 'Occasional blemishes',
    q4_frequent: 'Frequent blemishes',
    q5: 'Which age group are you in?',
    show_routine: 'Show my routine',
    result_eyebrow: 'Your skin check result',
    result_title: 'Your Bright routine',
    label_type: 'Skin type:',
    label_goal: 'Goal:',
    type_dry: 'Dry',
    type_normal: 'Normal',
    type_combination: 'Combination',
    type_oily: 'Oily',
    sensitive_suffix: ', sensitive',
    goal_firm: 'Firmness & fine lines',
    goal_tone: 'Even skin tone',
    goal_clear: 'Clearer skin',
    goal_calm: 'Less redness',
    goal_hydrate: 'Hydration & glow',
    goal_healthy: 'Healthy, balanced skin',
    result_lead: 'Follow the steps in order, morning and evening. A cosmetic recommendation, not a medical diagnosis.',
    morning: 'Morning',
    evening: 'Evening',
    morning_title: 'Your morning routine',
    evening_title: 'Your evening routine',
    why: 'Why for you:',
    shop: 'Shop',
    same_bottle: 'Same bottle as morning step {n}',
    spf_title: 'Sunscreen (SPF)',
    spf_desc: 'Essential every morning to protect your skin from UV rays.',
    spf_how: 'Use your own broad-spectrum sunscreen.',
    spf_why: 'daily UV protection keeps your routine’s results.',
    spf_note: 'Not part of the Bright line',
    desc_skinPad: 'Prepares the skin: gently exfoliates, soothes, hydrates and helps balance excess oil.',
    desc_essence: 'Light hydration that soothes and calms the skin. With PDRN, panthenol and centella.',
    desc_serum: 'Targeted care with PDRN 2%. Helps balance oil and even out skin tone; suitable for blemish-prone skin.',
    desc_ampoule: 'Intensive care for firmness and elasticity — our most concentrated PDRN formula.',
    desc_emulsion: 'Seals in moisture and helps reduce moisture loss. With PDRN, squalane and ceramide.',
    desc_sheetMask: 'A hydrating mask for extra care and a radiant look.',
    how_skinPad_am: 'Quick swipe over the face.',
    how_skinPad_pm: 'Swipe over the face. Extra care: leave on 10–20 min where needed.',
    how_skinPad_sensitive: 'Start 2–3 evenings a week for 10 minutes, then daily if your skin feels comfortable.',
    how_essence: 'Spread 1–2 pumps over the face.',
    how_serum: 'Spread 1–2 pumps over the face.',
    how_ampoule_essence: 'Replaces your Essence in the evening. Pat in until absorbed.',
    how_ampoule_serum: 'Replaces your Serum in the evening. Pat in until absorbed.',
    how_emulsion: 'Massage in gently as your last care step.',
    how_sheetMask: 'Leave on 10–20 min after the Skin Pad, then continue with step 2.',
    why_skinPad_am: 'a quick swipe keeps shine in check during the day.',
    why_skinPad_pm: 'the first step of every Bright evening.',
    why_skinPad_sensitive: 'a gentle start lets sensitive skin get used to it.',
    why_essence_sensitive: 'gentle, soothing hydration for sensitive skin.',
    why_essence_calm: 'you would like calmer skin with less redness.',
    why_essence_hydrate: 'you would like plump, hydrated skin.',
    why_essence_dry: 'dry skin needs gentle hydration, not oil control.',
    why_essence_normal: 'balanced skin needs gentle hydration, not oil control.',
    why_serum_tone: 'you would like a more even skin tone.',
    why_serum_clear: 'you would like clearer skin.',
    why_serum_oily: 'it helps balance oily skin.',
    why_serum_combination: 'it helps balance shine in your T-zone.',
    why_ampoule: 'you would like firmer, more elastic skin.',
    why_emulsion: 'the final step of every Bright routine.',
    extra_title: 'Extra care',
    extra_freq: '{f}× a week, in the evening after the Skin Pad',
    optional_title: 'Worth adding for mature skin',
    optional_lead: 'Use the Ampoule on some evenings in place of step 2 for intensive firming care.',
    note_sensitive: 'Sensitive skin: test new products on a small area first.',
    note_derm: 'If your skin is often strongly inflamed, please also see a dermatologist.',
    expect_title: 'What to expect',
    expect_lead: 'Clinically tested results of the products in your routine',
    after_once: 'After one use',
    after_1w: 'After 1 week',
    after_2w: 'After 2 weeks',
    after_4w: 'After 4 weeks',
    ex_ampoule: 'Surface skin elasticity with the Bright Ampoule',
    ex_emulsion: 'Surface skin hydration with the Bright Emulsion',
    ex_skinPad_cells: 'Visible dead skin cells with the Bright Skin Pad (leave-on use)',
    ex_skinPad_sebum: 'Sebum on the forehead with the Bright Skin Pad (leave-on use)',
    ex_serum_tone: 'Pigmentation area with the Bright Serum',
    ex_serum_clear: 'Inflammatory blemishes with the Bright Serum',
    ex_serum_oil: 'Skin oil with the Bright Serum',
    ex_essence: 'Redness after skin irritation with the Bright Essence (control: −2.41%)',
    fn_ampoule: 'Clinical study, 22 women aged 30–65, twice daily for 1 week (2025).',
    fn_emulsion: 'Clinical study, 22 participants aged 30+, 2 weeks (2025).',
    fn_skinPad: 'Clinical study, 20 participants, leave-on use twice daily for 4 weeks (2024).',
    fn_serum: 'Clinical study, 20 participants aged 19–59, twice daily for 4 weeks (2024).',
    fn_essence: 'Clinical study, 20 participants, single use after skin irritation (2024).',
    fn_all: 'Each product was tested on its own; individual results may vary.',
    choose_title: 'Choose how to start',
    recommended: 'Recommended',
    start_smaller: 'Start smaller',
    complete_title: 'Complete routine',
    essentials_title: 'The essentials',
    essentials_lead: 'The two steps that matter most for you. Add the rest later.',
    add_routine: 'Add routine to cart',
    add_essentials: 'Add essentials',
    products_count: '{n} products',
    adding: 'Adding…',
    add_error: 'Something went wrong. Please try again or add the products from their pages.',
    unavailable: 'Currently unavailable',
    retake: 'Retake skin check'
  },

  de: {
    eyebrow: 'Bright Hautcheck',
    start_title: 'Finden Sie Ihre Bright Routine',
    start_lead: 'Dauert etwa 2 Minuten. Ein Selfie und fünf kurze Fragen – dann Ihre Routine, abgestimmt auf Ihre Haut.',
    step1_t: 'Selfie aufnehmen',
    step1_d: 'Wir betrachten sichtbaren Glanz, Rötungen und die Ebenmäßigkeit des Hauttons.',
    step2_t: '5 kurze Fragen beantworten',
    step2_d: 'Wie sich Ihre Haut anfühlt und was Sie verändern möchten.',
    step3_t: 'Ihre Routine erhalten',
    step3_d: 'Morgens und abends, Schritt für Schritt, mit Bright Produkten.',
    start_cta: 'Hautcheck starten',
    skip_photo: 'Ohne Foto – nur Fragen',
    photo_stays: 'Ihr Foto verlässt Ihr Gerät nie',
    desktop_hint: 'Tipp: Handykameras liefern das beste Ergebnis. Öffnen Sie diese Seite auf Ihrem Handy oder nutzen Sie Ihre Webcam.',
    progress: 'Schritt {n} von 4 · {label}',
    p_privacy: 'Datenschutz',
    p_ready: 'Vorbereitung',
    p_photo: 'Ihr Foto',
    p_questions: 'Fragen',
    privacy_title: 'Bevor wir beginnen',
    privacy_lead: 'Ihre Privatsphäre ist uns wichtig. Das passiert mit Ihrem Foto.',
    privacy_1: 'Ihr Foto wird auf Ihrem Gerät analysiert. Es wird nie hochgeladen oder gespeichert.',
    privacy_2: 'Kein Konto nötig. Beim Schließen der Seite wird alles gelöscht.',
    privacy_3: 'Dies ist ein kosmetischer Hautcheck, keine medizinische Diagnose.',
    consent: 'Ich bin mindestens 16 Jahre alt und stimme zu, dass mein Foto für meinen Hautcheck auf diesem Gerät analysiert wird.',
    privacy_link: 'Datenschutzerklärung',
    continue: 'Weiter',
    back: 'Zurück',
    tips_title: 'Für das beste Ergebnis',
    tips_lead: 'Ein gutes Foto macht Ihre Routine genauer.',
    tip1_t: 'Gutes Tageslicht',
    tip1_d: 'Schauen Sie zum Fenster. Vermeiden Sie starke Schatten und Gegenlicht.',
    tip2_t: 'Reine Haut',
    tip2_d: 'Kein Make-up, damit wir Ihre Haut sehen, wie sie ist.',
    tip3_t: 'Freies Gesicht',
    tip3_d: 'Haare zurück, Brille ab.',
    tip4_t: 'Gerade schauen',
    tip4_d: 'Neutraler Ausdruck, Kamera auf Augenhöhe.',
    open_camera: 'Kamera öffnen',
    camera_note: 'Ihr Browser fragt nach dem Kamerazugriff.',
    preparing: 'Hautcheck wird vorbereitet…',
    camera_place: 'Platzieren Sie Ihr Gesicht im Oval',
    chip_light: 'Gutes Licht',
    chip_centre: 'Gesicht mittig',
    chip_still: 'Stillhalten',
    chip_noface: 'Gesicht wird gesucht…',
    take_photo: 'Foto aufnehmen',
    close: 'Schließen',
    camera_footer: 'Auf Ihrem Gerät analysiert · nie hochgeladen',
    analysing: 'Ihre Haut wird analysiert…',
    camera_error: 'Die Kamera konnte nicht gestartet werden. Sie können nur mit den Fragen fortfahren.',
    engine_error: 'Die Fotoanalyse ist gerade nicht verfügbar. Sie können nur mit den Fragen fortfahren.',
    noface_error: 'Auf dem Foto wurde kein Gesicht erkannt. Bitte versuchen Sie es bei gutem Licht erneut.',
    try_again: 'Erneut versuchen',
    questions_only: 'Nur mit Fragen fortfahren',
    seen_title: 'Das hat Ihr Hautcheck bemerkt',
    seen_lead: 'Basierend auf dem, was die Kamera sehen kann. Ihre Antworten bestimmen die Routine.',
    seen_shine: 'Glanz',
    seen_redness: 'Rötungen',
    seen_tone: 'Ebenmäßigkeit des Hauttons',
    lvl_shine_0: 'Gering',
    lvl_shine_1: 'Sichtbar in der T-Zone',
    lvl_shine_2: 'Überall sichtbar',
    lvl_red_0: 'Gering',
    lvl_red_1: 'Leicht',
    lvl_red_2: 'Deutlich',
    lvl_tone_0: 'Ebenmäßig',
    lvl_tone_1: 'Leicht uneben',
    lvl_tone_2: 'Uneben',
    seen_light: 'Das Licht war nicht ideal. Bitte prüfen Sie die vorausgewählten Antworten sorgfältig.',
    seen_cta: 'Weiter zu den Fragen',
    from_scan: 'Aus Ihrem Hautcheck',
    q_counter: 'Frage {n} von 5',
    choose_up_to_2: 'Bis zu 2 auswählen',
    q1: 'Direkt nach dem Gesichtwaschen, bevor Sie etwas auftragen: Wie fühlt sich Ihre Haut an?',
    q1_dry: 'Gespannt, manchmal schuppig',
    q1_normal: 'Angenehm',
    q1_combination: 'Angenehm, aber nach einigen Stunden glänzend an Stirn, Nase und Kinn',
    q1_oily: 'Nach einigen Stunden überall glänzend',
    q2: 'Wie reagiert Ihre Haut auf neue Produkte, Sonne oder Kälte?',
    q2_calm: 'Ruhig, sie reagiert selten',
    q2_some: 'Manchmal leicht gerötet',
    q2_often: 'Oft gerötet, brennend oder juckend',
    q3: 'Was möchten Sie in vier Wochen im Spiegel sehen?',
    q3_firm: 'Straffere, elastischere Haut',
    q3_tone: 'Einen ebenmäßigeren Hautton',
    q3_clear: 'Klarere Haut, weniger Unreinheiten',
    q3_calm: 'Ruhigere Haut, weniger Rötungen',
    q3_hydrate: 'Pralle, durchfeuchtete, strahlende Haut',
    q3_healthy: 'Einfach gesunde, ausgeglichene Haut',
    q4: 'Wie sehen Ihre Poren und Unreinheiten an den meisten Tagen aus?',
    q4_barely: 'Kaum sichtbar',
    q4_visible: 'Poren sichtbar an Nase und Wangen',
    q4_occasional: 'Gelegentlich Unreinheiten',
    q4_frequent: 'Häufig Unreinheiten',
    q5: 'Zu welcher Altersgruppe gehören Sie?',
    show_routine: 'Meine Routine anzeigen',
    result_eyebrow: 'Ihr Hautcheck-Ergebnis',
    result_title: 'Ihre Bright Routine',
    label_type: 'Hauttyp:',
    label_goal: 'Ziel:',
    type_dry: 'Trocken',
    type_normal: 'Normal',
    type_combination: 'Mischhaut',
    type_oily: 'Fettig',
    sensitive_suffix: ', empfindlich',
    goal_firm: 'Straffheit & feine Linien',
    goal_tone: 'Ebenmäßiger Hautton',
    goal_clear: 'Klarere Haut',
    goal_calm: 'Weniger Rötungen',
    goal_hydrate: 'Feuchtigkeit & Ausstrahlung',
    goal_healthy: 'Gesunde, ausgeglichene Haut',
    result_lead: 'Folgen Sie den Schritten der Reihe nach, morgens und abends. Eine kosmetische Empfehlung, keine medizinische Diagnose.',
    morning: 'Morgens',
    evening: 'Abends',
    morning_title: 'Ihre Morgenroutine',
    evening_title: 'Ihre Abendroutine',
    why: 'Warum für Sie:',
    shop: 'Shop',
    same_bottle: 'Dieselbe Flasche wie Schritt {n} am Morgen',
    spf_title: 'Sonnenschutz (LSF)',
    spf_desc: 'Jeden Morgen unverzichtbar, um Ihre Haut vor UV-Strahlen zu schützen.',
    spf_how: 'Verwenden Sie Ihren eigenen Breitband-Sonnenschutz.',
    spf_why: 'täglicher UV-Schutz bewahrt die Ergebnisse Ihrer Routine.',
    spf_note: 'Nicht Teil der Bright Linie',
    desc_skinPad: 'Bereitet die Haut vor: peelt sanft, beruhigt, spendet Feuchtigkeit und hilft, überschüssigen Talg auszugleichen.',
    desc_essence: 'Leichte Feuchtigkeit, die die Haut beruhigt. Mit PDRN, Panthenol und Centella.',
    desc_serum: 'Gezielte Pflege mit 2 % PDRN. Hilft, Talg auszugleichen und den Hautton zu verfeinern; geeignet für unreine Haut.',
    desc_ampoule: 'Intensivpflege für Straffheit und Elastizität – unsere am höchsten konzentrierte PDRN-Formel.',
    desc_emulsion: 'Schließt Feuchtigkeit ein und hilft, Feuchtigkeitsverlust zu reduzieren. Mit PDRN, Squalan und Ceramid.',
    desc_sheetMask: 'Eine Feuchtigkeitsmaske für extra Pflege und einen strahlenden Teint.',
    how_skinPad_am: 'Kurz über das Gesicht streichen.',
    how_skinPad_pm: 'Über das Gesicht streichen. Extra Pflege: 10–20 Min. auf den gewünschten Stellen einwirken lassen.',
    how_skinPad_sensitive: 'Beginnen Sie mit 2–3 Abenden pro Woche für 10 Minuten, dann täglich, wenn sich Ihre Haut wohlfühlt.',
    how_essence: '1–2 Pumpstöße auf dem Gesicht verteilen.',
    how_serum: '1–2 Pumpstöße auf dem Gesicht verteilen.',
    how_ampoule_essence: 'Ersetzt abends Ihre Essence. Einklopfen, bis sie eingezogen ist.',
    how_ampoule_serum: 'Ersetzt abends Ihr Serum. Einklopfen, bis es eingezogen ist.',
    how_emulsion: 'Als letzten Pflegeschritt sanft einmassieren.',
    how_sheetMask: 'Nach dem Skin Pad 10–20 Min. einwirken lassen, dann mit Schritt 2 weitermachen.',
    why_skinPad_am: 'ein kurzes Abwischen hält Glanz tagsüber in Schach.',
    why_skinPad_pm: 'der erste Schritt jedes Bright Abends.',
    why_skinPad_sensitive: 'ein sanfter Start lässt empfindliche Haut sich daran gewöhnen.',
    why_essence_sensitive: 'sanfte, beruhigende Feuchtigkeit für empfindliche Haut.',
    why_essence_calm: 'Sie wünschen sich ruhigere Haut mit weniger Rötungen.',
    why_essence_hydrate: 'Sie wünschen sich pralle, durchfeuchtete Haut.',
    why_essence_dry: 'trockene Haut braucht sanfte Feuchtigkeit, keine Talgregulierung.',
    why_essence_normal: 'ausgeglichene Haut braucht sanfte Feuchtigkeit, keine Talgregulierung.',
    why_serum_tone: 'Sie wünschen sich einen ebenmäßigeren Hautton.',
    why_serum_clear: 'Sie wünschen sich klarere Haut.',
    why_serum_oily: 'es hilft, fettige Haut auszugleichen.',
    why_serum_combination: 'es hilft, Glanz in Ihrer T-Zone auszugleichen.',
    why_ampoule: 'Sie wünschen sich straffere, elastischere Haut.',
    why_emulsion: 'der letzte Schritt jeder Bright Routine.',
    extra_title: 'Extra Pflege',
    extra_freq: '{f}× pro Woche, abends nach dem Skin Pad',
    optional_title: 'Eine Empfehlung für reife Haut',
    optional_lead: 'Verwenden Sie die Ampoule an manchen Abenden anstelle von Schritt 2 für eine intensive straffende Pflege.',
    note_sensitive: 'Empfindliche Haut: Testen Sie neue Produkte zuerst an einer kleinen Stelle.',
    note_derm: 'Wenn Ihre Haut oft stark entzündet ist, wenden Sie sich bitte auch an eine Dermatologin oder einen Dermatologen.',
    expect_title: 'Was Sie erwarten können',
    expect_lead: 'Klinisch getestete Ergebnisse der Produkte in Ihrer Routine',
    after_once: 'Nach einer Anwendung',
    after_1w: 'Nach 1 Woche',
    after_2w: 'Nach 2 Wochen',
    after_4w: 'Nach 4 Wochen',
    ex_ampoule: 'Elastizität der Hautoberfläche mit der Bright Ampoule',
    ex_emulsion: 'Feuchtigkeit der Hautoberfläche mit der Bright Emulsion',
    ex_skinPad_cells: 'Sichtbare abgestorbene Hautzellen mit dem Bright Skin Pad (mit Einwirkzeit)',
    ex_skinPad_sebum: 'Talg auf der Stirn mit dem Bright Skin Pad (mit Einwirkzeit)',
    ex_serum_tone: 'Pigmentierte Fläche mit dem Bright Serum',
    ex_serum_clear: 'Entzündliche Unreinheiten mit dem Bright Serum',
    ex_serum_oil: 'Hautfett mit dem Bright Serum',
    ex_essence: 'Rötung nach Hautreizung mit der Bright Essence (Kontrolle: −2,41 %)',
    fn_ampoule: 'Klinische Studie, 22 Frauen im Alter von 30–65 Jahren, 2× täglich über 1 Woche (2025).',
    fn_emulsion: 'Klinische Studie, 22 Teilnehmende ab 30 Jahren, 2 Wochen (2025).',
    fn_skinPad: 'Klinische Studie, 20 Teilnehmende, mit Einwirkzeit 2× täglich über 4 Wochen (2024).',
    fn_serum: 'Klinische Studie, 20 Teilnehmende im Alter von 19–59 Jahren, 2× täglich über 4 Wochen (2024).',
    fn_essence: 'Klinische Studie, 20 Teilnehmende, einmalige Anwendung nach Hautreizung (2024).',
    fn_all: 'Jedes Produkt wurde einzeln getestet; die individuellen Ergebnisse können variieren.',
    choose_title: 'Wie möchten Sie starten?',
    recommended: 'Empfohlen',
    start_smaller: 'Klein starten',
    complete_title: 'Komplette Routine',
    essentials_title: 'Die Basics',
    essentials_lead: 'Die zwei wichtigsten Schritte für Sie. Den Rest können Sie später ergänzen.',
    add_routine: 'Routine in den Warenkorb',
    add_essentials: 'Basics hinzufügen',
    products_count: '{n} Produkte',
    adding: 'Wird hinzugefügt…',
    add_error: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder legen Sie die Produkte auf ihren Seiten in den Warenkorb.',
    unavailable: 'Derzeit nicht verfügbar',
    retake: 'Hautcheck wiederholen'
  },

  hr: {
    eyebrow: 'Bright analiza kože',
    start_title: 'Pronađite svoju Bright rutinu',
    start_lead: 'Traje oko 2 minute. Jedan selfie i pet kratkih pitanja – zatim vaša rutina, prilagođena vašoj koži.',
    step1_t: 'Snimite selfie',
    step1_d: 'Promatramo vidljivi sjaj, crvenilo i ujednačenost tena.',
    step2_t: 'Odgovorite na 5 kratkih pitanja',
    step2_d: 'Kako se vaša koža osjeća i što biste željeli promijeniti.',
    step3_t: 'Dobijte svoju rutinu',
    step3_d: 'Ujutro i navečer, korak po korak, s Bright proizvodima.',
    start_cta: 'Započni analizu kože',
    skip_photo: 'Bez fotografije – samo pitanja',
    photo_stays: 'Vaša fotografija nikada ne napušta vaš uređaj',
    desktop_hint: 'Savjet: kamere mobitela daju najbolji rezultat. Otvorite ovu stranicu na mobitelu ili koristite web-kameru.',
    progress: 'Korak {n} od 4 · {label}',
    p_privacy: 'Privatnost',
    p_ready: 'Priprema',
    p_photo: 'Vaša fotografija',
    p_questions: 'Pitanja',
    privacy_title: 'Prije nego što počnemo',
    privacy_lead: 'Vaša nam je privatnost važna. Evo što se događa s vašom fotografijom.',
    privacy_1: 'Vaša se fotografija analizira na vašem uređaju. Nikada se ne prenosi niti pohranjuje.',
    privacy_2: 'Nije potreban korisnički račun. Zatvaranjem stranice sve se briše.',
    privacy_3: 'Ovo je kozmetička analiza kože, a ne medicinska dijagnoza.',
    consent: 'Imam najmanje 16 godina i pristajem da se moja fotografija za analizu kože analizira na ovom uređaju.',
    privacy_link: 'Pravila privatnosti',
    continue: 'Nastavi',
    back: 'Natrag',
    tips_title: 'Za najbolji rezultat',
    tips_lead: 'Dobra fotografija čini vašu rutinu preciznijom.',
    tip1_t: 'Dobro dnevno svjetlo',
    tip1_d: 'Okrenite se prema prozoru. Izbjegavajte jake sjene i svjetlo iza leđa.',
    tip2_t: 'Čista koža',
    tip2_d: 'Bez šminke, kako bismo vidjeli vašu kožu kakva jest.',
    tip3_t: 'Otkriveno lice',
    tip3_d: 'Kosa straga, bez naočala.',
    tip4_t: 'Gledajte ravno',
    tip4_d: 'Neutralan izraz lica, kamera u razini očiju.',
    open_camera: 'Otvori kameru',
    camera_note: 'Preglednik će zatražiti pristup kameri.',
    preparing: 'Pripremamo analizu kože…',
    camera_place: 'Smjestite lice unutar ovala',
    chip_light: 'Dobro svjetlo',
    chip_centre: 'Lice u sredini',
    chip_still: 'Budite mirni',
    chip_noface: 'Tražimo vaše lice…',
    take_photo: 'Snimi fotografiju',
    close: 'Zatvori',
    camera_footer: 'Analizira se na vašem uređaju · nikada se ne prenosi',
    analysing: 'Analiziramo vašu kožu…',
    camera_error: 'Kameru nije bilo moguće pokrenuti. Možete nastaviti samo s pitanjima.',
    engine_error: 'Analiza fotografije trenutačno nije dostupna. Možete nastaviti samo s pitanjima.',
    noface_error: 'Na fotografiji nismo pronašli lice. Pokušajte ponovno uz dobro svjetlo.',
    try_again: 'Pokušaj ponovno',
    questions_only: 'Nastavi samo s pitanjima',
    seen_title: 'Što je analiza kože primijetila',
    seen_lead: 'Na temelju onoga što kamera može vidjeti. Vaši odgovori određuju rutinu.',
    seen_shine: 'Sjaj',
    seen_redness: 'Crvenilo',
    seen_tone: 'Ujednačenost tena',
    lvl_shine_0: 'Nizak',
    lvl_shine_1: 'Vidljiv u T-zoni',
    lvl_shine_2: 'Vidljiv posvuda',
    lvl_red_0: 'Nisko',
    lvl_red_1: 'Blago',
    lvl_red_2: 'Izraženo',
    lvl_tone_0: 'Ujednačen',
    lvl_tone_1: 'Blago neujednačen',
    lvl_tone_2: 'Neujednačen',
    seen_light: 'Svjetlo nije bilo idealno, stoga pažljivo provjerite unaprijed odabrane odgovore.',
    seen_cta: 'Nastavi na pitanja',
    from_scan: 'Iz vaše analize kože',
    q_counter: 'Pitanje {n} od 5',
    choose_up_to_2: 'Odaberite do 2',
    q1: 'Odmah nakon umivanja, prije nanošenja bilo čega, kako se osjeća vaša koža?',
    q1_dry: 'Zategnuto, ponekad se ljušti',
    q1_normal: 'Ugodno',
    q1_combination: 'Ugodno, ali nakon nekoliko sati sjaji na čelu, nosu i bradi',
    q1_oily: 'Nakon nekoliko sati sjaji posvuda',
    q2: 'Kako vaša koža reagira na nove proizvode, sunce ili hladnoću?',
    q2_calm: 'Mirno, rijetko reagira',
    q2_some: 'Ponekad blago pocrveni',
    q2_often: 'Često pocrveni, peče ili svrbi',
    q3: 'Što biste voljeli vidjeti u ogledalu za četiri tjedna?',
    q3_firm: 'Čvršću, elastičniju kožu',
    q3_tone: 'Ujednačeniji ten',
    q3_clear: 'Čišću kožu, manje nepravilnosti',
    q3_calm: 'Smireniju kožu, manje crvenila',
    q3_hydrate: 'Punu, hidratiziranu, blistavu kožu',
    q3_healthy: 'Jednostavno zdravu, uravnoteženu kožu',
    q4: 'Kako vaše pore i nepravilnosti izgledaju većinu dana?',
    q4_barely: 'Jedva vidljive',
    q4_visible: 'Pore vidljive na nosu i obrazima',
    q4_occasional: 'Povremene nepravilnosti',
    q4_frequent: 'Česte nepravilnosti',
    q5: 'Kojoj dobnoj skupini pripadate?',
    show_routine: 'Prikaži moju rutinu',
    result_eyebrow: 'Rezultat vaše analize kože',
    result_title: 'Vaša Bright rutina',
    label_type: 'Tip kože:',
    label_goal: 'Cilj:',
    type_dry: 'Suha',
    type_normal: 'Normalna',
    type_combination: 'Mješovita',
    type_oily: 'Masna',
    sensitive_suffix: ', osjetljiva',
    goal_firm: 'Čvrstoća i fine linije',
    goal_tone: 'Ujednačen ten',
    goal_clear: 'Čišća koža',
    goal_calm: 'Manje crvenila',
    goal_hydrate: 'Hidratacija i sjaj',
    goal_healthy: 'Zdrava, uravnotežena koža',
    result_lead: 'Slijedite korake redom, ujutro i navečer. Kozmetička preporuka, a ne medicinska dijagnoza.',
    morning: 'Jutro',
    evening: 'Večer',
    morning_title: 'Vaša jutarnja rutina',
    evening_title: 'Vaša večernja rutina',
    why: 'Zašto za vas:',
    shop: 'Kupi',
    same_bottle: 'Ista bočica kao jutarnji korak {n}',
    spf_title: 'Zaštita od sunca (SPF)',
    spf_desc: 'Neophodna svako jutro za zaštitu kože od UV zraka.',
    spf_how: 'Koristite vlastitu zaštitu širokog spektra.',
    spf_why: 'svakodnevna UV zaštita čuva rezultate vaše rutine.',
    spf_note: 'Nije dio Bright linije',
    desc_skinPad: 'Priprema kožu: nježno eksfolira, umiruje, hidratizira i pomaže uravnotežiti višak sebuma.',
    desc_essence: 'Lagana hidratacija koja umiruje kožu. S PDRN-om, pantenolom i centelom.',
    desc_serum: 'Ciljana njega s 2 % PDRN-a. Pomaže uravnotežiti sebum i ujednačiti ten; prikladan za kožu sklonu nepravilnostima.',
    desc_ampoule: 'Intenzivna njega za čvrstoću i elastičnost – naša najkoncentriranija PDRN formula.',
    desc_emulsion: 'Zadržava vlagu i pomaže smanjiti gubitak vlage. S PDRN-om, skvalanom i ceramidom.',
    desc_sheetMask: 'Hidratantna maska za dodatnu njegu i blistav izgled.',
    how_skinPad_am: 'Kratko prijeđite preko lica.',
    how_skinPad_pm: 'Prijeđite preko lica. Dodatna njega: ostavite 10–20 min na željenim mjestima.',
    how_skinPad_sensitive: 'Počnite s 2–3 večeri tjedno po 10 minuta, zatim svakodnevno ako se koža osjeća ugodno.',
    how_essence: 'Nanesite 1–2 potiska na lice.',
    how_serum: 'Nanesite 1–2 potiska na lice.',
    how_ampoule_essence: 'Navečer zamjenjuje vašu Essence. Utapkajte dok se ne upije.',
    how_ampoule_serum: 'Navečer zamjenjuje vaš Serum. Utapkajte dok se ne upije.',
    how_emulsion: 'Nježno umasirajte kao posljednji korak njege.',
    how_sheetMask: 'Ostavite 10–20 min nakon Skin Pada, zatim nastavite s korakom 2.',
    why_skinPad_am: 'kratko brisanje drži sjaj pod kontrolom tijekom dana.',
    why_skinPad_pm: 'prvi korak svake Bright večeri.',
    why_skinPad_sensitive: 'nježan početak omogućuje osjetljivoj koži da se navikne.',
    why_essence_sensitive: 'nježna, umirujuća hidratacija za osjetljivu kožu.',
    why_essence_calm: 'želite smireniju kožu s manje crvenila.',
    why_essence_hydrate: 'želite punu, hidratiziranu kožu.',
    why_essence_dry: 'suhoj koži treba nježna hidratacija, a ne kontrola sebuma.',
    why_essence_normal: 'uravnoteženoj koži treba nježna hidratacija, a ne kontrola sebuma.',
    why_serum_tone: 'želite ujednačeniji ten.',
    why_serum_clear: 'želite čišću kožu.',
    why_serum_oily: 'pomaže uravnotežiti masnu kožu.',
    why_serum_combination: 'pomaže uravnotežiti sjaj u T-zoni.',
    why_ampoule: 'želite čvršću, elastičniju kožu.',
    why_emulsion: 'posljednji korak svake Bright rutine.',
    extra_title: 'Dodatna njega',
    extra_freq: '{f}× tjedno, navečer nakon Skin Pada',
    optional_title: 'Preporuka za zrelu kožu',
    optional_lead: 'Neke večeri koristite Ampoule umjesto koraka 2 za intenzivnu njegu za čvrstoću kože.',
    note_sensitive: 'Osjetljiva koža: nove proizvode najprije isprobajte na maloj površini.',
    note_derm: 'Ako je vaša koža često jako upaljena, obratite se i dermatologu.',
    expect_title: 'Što možete očekivati',
    expect_lead: 'Klinički testirani rezultati proizvoda u vašoj rutini',
    after_once: 'Nakon jedne primjene',
    after_1w: 'Nakon 1 tjedna',
    after_2w: 'Nakon 2 tjedna',
    after_4w: 'Nakon 4 tjedna',
    ex_ampoule: 'Elastičnost površine kože uz Bright Ampoule',
    ex_emulsion: 'Hidratacija površine kože uz Bright Emulsion',
    ex_skinPad_cells: 'Vidljive odumrle stanice kože uz Bright Skin Pad (uz vrijeme djelovanja)',
    ex_skinPad_sebum: 'Sebum na čelu uz Bright Skin Pad (uz vrijeme djelovanja)',
    ex_serum_tone: 'Površina pigmentacije uz Bright Serum',
    ex_serum_clear: 'Upalne nepravilnosti uz Bright Serum',
    ex_serum_oil: 'Masnoća kože uz Bright Serum',
    ex_essence: 'Crvenilo nakon iritacije kože uz Bright Essence (kontrola: −2,41 %)',
    fn_ampoule: 'Klinička studija, 22 žene u dobi od 30 do 65 godina, 2× dnevno tijekom 1 tjedna (2025.).',
    fn_emulsion: 'Klinička studija, 22 ispitanika starija od 30 godina, 2 tjedna (2025.).',
    fn_skinPad: 'Klinička studija, 20 ispitanika, uz vrijeme djelovanja 2× dnevno tijekom 4 tjedna (2024.).',
    fn_serum: 'Klinička studija, 20 ispitanika u dobi od 19 do 59 godina, 2× dnevno tijekom 4 tjedna (2024.).',
    fn_essence: 'Klinička studija, 20 ispitanika, jednokratna primjena nakon iritacije kože (2024.).',
    fn_all: 'Svaki je proizvod testiran zasebno; pojedinačni rezultati mogu se razlikovati.',
    choose_title: 'Odaberite kako započeti',
    recommended: 'Preporučeno',
    start_smaller: 'Započnite manjim',
    complete_title: 'Cjelovita rutina',
    essentials_title: 'Osnove',
    essentials_lead: 'Dva najvažnija koraka za vas. Ostalo možete dodati kasnije.',
    add_routine: 'Dodaj rutinu u košaricu',
    add_essentials: 'Dodaj osnove',
    products_count: 'Proizvoda: {n}',
    adding: 'Dodajemo…',
    add_error: 'Nešto nije u redu. Pokušajte ponovno ili dodajte proizvode s njihovih stranica.',
    unavailable: 'Trenutačno nedostupno',
    retake: 'Ponovi analizu kože'
  },

  fr: {
    eyebrow: 'Diagnostic de peau Bright',
    start_title: 'Trouvez votre routine Bright',
    start_lead: 'Environ 2 minutes. Un selfie et cinq questions courtes – puis votre routine, conçue pour votre peau.',
    step1_t: 'Prenez un selfie',
    step1_d: 'Nous observons la brillance visible, les rougeurs et l’uniformité du teint.',
    step2_t: 'Répondez à 5 questions courtes',
    step2_d: 'Comment votre peau se sent et ce que vous aimeriez changer.',
    step3_t: 'Recevez votre routine',
    step3_d: 'Matin et soir, étape par étape, avec les produits Bright.',
    start_cta: 'Commencer le diagnostic',
    skip_photo: 'Sans photo – questions uniquement',
    photo_stays: 'Votre photo ne quitte jamais votre appareil',
    desktop_hint: 'Astuce : la caméra d’un téléphone donne le meilleur résultat. Ouvrez cette page sur votre téléphone ou utilisez votre webcam.',
    progress: 'Étape {n} sur 4 · {label}',
    p_privacy: 'Confidentialité',
    p_ready: 'Préparation',
    p_photo: 'Votre photo',
    p_questions: 'Questions',
    privacy_title: 'Avant de commencer',
    privacy_lead: 'Votre vie privée compte pour nous. Voici ce qu’il advient de votre photo.',
    privacy_1: 'Votre photo est analysée sur votre appareil. Elle n’est jamais envoyée ni enregistrée.',
    privacy_2: 'Aucun compte nécessaire. Fermer la page efface tout.',
    privacy_3: 'Il s’agit d’un diagnostic cosmétique, pas d’un diagnostic médical.',
    consent: 'J’ai au moins 16 ans et j’accepte que ma photo soit analysée sur cet appareil pour mon diagnostic de peau.',
    privacy_link: 'Politique de confidentialité',
    continue: 'Continuer',
    back: 'Retour',
    tips_title: 'Pour un meilleur résultat',
    tips_lead: 'Une bonne photo rend votre routine plus précise.',
    tip1_t: 'Bonne lumière du jour',
    tip1_d: 'Placez-vous face à une fenêtre. Évitez les ombres marquées et le contre-jour.',
    tip2_t: 'Peau nette',
    tip2_d: 'Sans maquillage, pour voir votre peau telle qu’elle est.',
    tip3_t: 'Visage dégagé',
    tip3_d: 'Cheveux en arrière, sans lunettes.',
    tip4_t: 'Regard droit',
    tip4_d: 'Expression neutre, caméra à hauteur des yeux.',
    open_camera: 'Ouvrir la caméra',
    camera_note: 'Votre navigateur vous demandera l’accès à la caméra.',
    preparing: 'Préparation du diagnostic…',
    camera_place: 'Placez votre visage dans l’ovale',
    chip_light: 'Bonne lumière',
    chip_centre: 'Visage centré',
    chip_still: 'Ne bougez plus',
    chip_noface: 'Recherche de votre visage…',
    take_photo: 'Prendre la photo',
    close: 'Fermer',
    camera_footer: 'Analysée sur votre appareil · jamais envoyée',
    analysing: 'Analyse de votre peau…',
    camera_error: 'Impossible de démarrer la caméra. Vous pouvez continuer avec les questions uniquement.',
    engine_error: 'L’analyse photo n’est pas disponible pour le moment. Vous pouvez continuer avec les questions uniquement.',
    noface_error: 'Aucun visage n’a été détecté sur la photo. Veuillez réessayer avec une bonne lumière.',
    try_again: 'Réessayer',
    questions_only: 'Continuer avec les questions uniquement',
    seen_title: 'Ce que votre diagnostic a remarqué',
    seen_lead: 'D’après ce que la caméra peut voir. Vos réponses déterminent la routine.',
    seen_shine: 'Brillance',
    seen_redness: 'Rougeurs',
    seen_tone: 'Uniformité du teint',
    lvl_shine_0: 'Faible',
    lvl_shine_1: 'Visible sur la zone T',
    lvl_shine_2: 'Visible partout',
    lvl_red_0: 'Faibles',
    lvl_red_1: 'Légères',
    lvl_red_2: 'Marquées',
    lvl_tone_0: 'Uniforme',
    lvl_tone_1: 'Légèrement irrégulier',
    lvl_tone_2: 'Irrégulier',
    seen_light: 'La lumière n’était pas idéale : vérifiez bien les réponses présélectionnées.',
    seen_cta: 'Continuer vers les questions',
    from_scan: 'Selon votre diagnostic',
    q_counter: 'Question {n} sur 5',
    choose_up_to_2: 'Choisissez-en jusqu’à 2',
    q1: 'Juste après avoir lavé votre visage, avant d’appliquer quoi que ce soit, comment se sent votre peau ?',
    q1_dry: 'Tiraillée, parfois avec des squames',
    q1_normal: 'Confortable',
    q1_combination: 'Confortable, mais brillante sur le front, le nez et le menton après quelques heures',
    q1_oily: 'Brillante partout après quelques heures',
    q2: 'Comment votre peau réagit-elle aux nouveaux produits, au soleil ou au froid ?',
    q2_calm: 'Calme, elle réagit rarement',
    q2_some: 'Parfois un peu rouge',
    q2_often: 'Souvent rouge, avec picotements ou démangeaisons',
    q3: 'Qu’aimeriez-vous voir dans votre miroir dans quatre semaines ?',
    q3_firm: 'Une peau plus ferme et plus élastique',
    q3_tone: 'Un teint plus uniforme',
    q3_clear: 'Une peau plus nette, moins d’imperfections',
    q3_calm: 'Une peau plus apaisée, moins de rougeurs',
    q3_hydrate: 'Une peau rebondie, hydratée et lumineuse',
    q3_healthy: 'Simplement une peau saine et équilibrée',
    q4: 'À quoi ressemblent vos pores et vos imperfections la plupart du temps ?',
    q4_barely: 'À peine visibles',
    q4_visible: 'Pores visibles sur le nez et les joues',
    q4_occasional: 'Imperfections occasionnelles',
    q4_frequent: 'Imperfections fréquentes',
    q5: 'Quelle est votre tranche d’âge ?',
    show_routine: 'Voir ma routine',
    result_eyebrow: 'Résultat de votre diagnostic',
    result_title: 'Votre routine Bright',
    label_type: 'Type de peau :',
    label_goal: 'Objectif :',
    type_dry: 'Sèche',
    type_normal: 'Normale',
    type_combination: 'Mixte',
    type_oily: 'Grasse',
    sensitive_suffix: ', sensible',
    goal_firm: 'Fermeté et ridules',
    goal_tone: 'Teint uniforme',
    goal_clear: 'Peau plus nette',
    goal_calm: 'Moins de rougeurs',
    goal_hydrate: 'Hydratation et éclat',
    goal_healthy: 'Peau saine et équilibrée',
    result_lead: 'Suivez les étapes dans l’ordre, matin et soir. Une recommandation cosmétique, pas un diagnostic médical.',
    morning: 'Matin',
    evening: 'Soir',
    morning_title: 'Votre routine du matin',
    evening_title: 'Votre routine du soir',
    why: 'Pourquoi pour vous :',
    shop: 'Acheter',
    same_bottle: 'Même flacon qu’à l’étape {n} du matin',
    spf_title: 'Protection solaire (SPF)',
    spf_desc: 'Indispensable chaque matin pour protéger votre peau des UV.',
    spf_how: 'Utilisez votre propre protection solaire à large spectre.',
    spf_why: 'une protection UV quotidienne préserve les résultats de votre routine.',
    spf_note: 'Ne fait pas partie de la gamme Bright',
    desc_skinPad: 'Prépare la peau : exfolie en douceur, apaise, hydrate et aide à équilibrer l’excès de sébum.',
    desc_essence: 'Une hydratation légère qui apaise la peau. Au PDRN, panthénol et centella.',
    desc_serum: 'Un soin ciblé au PDRN 2 %. Aide à équilibrer le sébum et à unifier le teint ; adapté aux peaux à imperfections.',
    desc_ampoule: 'Un soin intensif pour la fermeté et l’élasticité – notre formule au PDRN la plus concentrée.',
    desc_emulsion: 'Retient l’hydratation et aide à réduire la perte en eau. Au PDRN, squalane et céramide.',
    desc_sheetMask: 'Un masque hydratant pour un soin supplémentaire et un teint lumineux.',
    how_skinPad_am: 'Passez rapidement sur le visage.',
    how_skinPad_pm: 'Passez sur le visage. Soin intensif : laissez poser 10–20 min aux endroits souhaités.',
    how_skinPad_sensitive: 'Commencez 2–3 soirs par semaine pendant 10 minutes, puis chaque jour si votre peau est à l’aise.',
    how_essence: 'Répartissez 1–2 pressions sur le visage.',
    how_serum: 'Répartissez 1–2 pressions sur le visage.',
    how_ampoule_essence: 'Remplace votre Essence le soir. Tapotez jusqu’à absorption.',
    how_ampoule_serum: 'Remplace votre Serum le soir. Tapotez jusqu’à absorption.',
    how_emulsion: 'Massez délicatement comme dernière étape de soin.',
    how_sheetMask: 'Laissez poser 10–20 min après le Skin Pad, puis passez à l’étape 2.',
    why_skinPad_am: 'un passage rapide limite la brillance pendant la journée.',
    why_skinPad_pm: 'la première étape de chaque soirée Bright.',
    why_skinPad_sensitive: 'un démarrage en douceur permet à la peau sensible de s’habituer.',
    why_essence_sensitive: 'une hydratation douce et apaisante pour peau sensible.',
    why_essence_calm: 'vous souhaitez une peau plus apaisée, avec moins de rougeurs.',
    why_essence_hydrate: 'vous souhaitez une peau rebondie et hydratée.',
    why_essence_dry: 'une peau sèche a besoin d’une hydratation douce, pas d’un contrôle du sébum.',
    why_essence_normal: 'une peau équilibrée a besoin d’une hydratation douce, pas d’un contrôle du sébum.',
    why_serum_tone: 'vous souhaitez un teint plus uniforme.',
    why_serum_clear: 'vous souhaitez une peau plus nette.',
    why_serum_oily: 'il aide à équilibrer la peau grasse.',
    why_serum_combination: 'il aide à équilibrer la brillance de la zone T.',
    why_ampoule: 'vous souhaitez une peau plus ferme et plus élastique.',
    why_emulsion: 'la dernière étape de chaque routine Bright.',
    extra_title: 'Soin supplémentaire',
    extra_freq: '{f}× par semaine, le soir après le Skin Pad',
    optional_title: 'Conseillé pour les peaux matures',
    optional_lead: 'Utilisez l’Ampoule certains soirs à la place de l’étape 2 pour un soin raffermissant intensif.',
    note_sensitive: 'Peau sensible : testez d’abord les nouveaux produits sur une petite zone.',
    note_derm: 'Si votre peau est souvent très enflammée, consultez également un dermatologue.',
    expect_title: 'À quoi vous attendre',
    expect_lead: 'Résultats cliniquement testés des produits de votre routine',
    after_once: 'Après une application',
    after_1w: 'Après 1 semaine',
    after_2w: 'Après 2 semaines',
    after_4w: 'Après 4 semaines',
    ex_ampoule: 'Élasticité de la surface de la peau avec la Bright Ampoule',
    ex_emulsion: 'Hydratation de la surface de la peau avec la Bright Emulsion',
    ex_skinPad_cells: 'Cellules mortes visibles avec le Bright Skin Pad (en temps de pose)',
    ex_skinPad_sebum: 'Sébum sur le front avec le Bright Skin Pad (en temps de pose)',
    ex_serum_tone: 'Surface pigmentée avec le Bright Serum',
    ex_serum_clear: 'Imperfections inflammatoires avec le Bright Serum',
    ex_serum_oil: 'Sébum de la peau avec le Bright Serum',
    ex_essence: 'Rougeurs après irritation cutanée avec la Bright Essence (témoin : −2,41 %)',
    fn_ampoule: 'Étude clinique, 22 femmes de 30 à 65 ans, 2 fois par jour pendant 1 semaine (2025).',
    fn_emulsion: 'Étude clinique, 22 participants de 30 ans et plus, 2 semaines (2025).',
    fn_skinPad: 'Étude clinique, 20 participants, en temps de pose 2 fois par jour pendant 4 semaines (2024).',
    fn_serum: 'Étude clinique, 20 participants de 19 à 59 ans, 2 fois par jour pendant 4 semaines (2024).',
    fn_essence: 'Étude clinique, 20 participants, application unique après irritation cutanée (2024).',
    fn_all: 'Chaque produit a été testé séparément ; les résultats individuels peuvent varier.',
    choose_title: 'Choisissez comment commencer',
    recommended: 'Recommandé',
    start_smaller: 'Commencer en douceur',
    complete_title: 'Routine complète',
    essentials_title: 'L’essentiel',
    essentials_lead: 'Les deux étapes les plus importantes pour vous. Ajoutez le reste plus tard.',
    add_routine: 'Ajouter la routine au panier',
    add_essentials: 'Ajouter l’essentiel',
    products_count: '{n} produits',
    adding: 'Ajout en cours…',
    add_error: 'Une erreur s’est produite. Veuillez réessayer ou ajouter les produits depuis leur page.',
    unavailable: 'Actuellement indisponible',
    retake: 'Refaire le diagnostic'
  }
};

export function makeT(lang) {
  const dict = I18N[lang] || I18N.en;
  return (key, vars) => {
    let s = dict[key] ?? I18N.en[key] ?? key;
    if (vars) for (const k of Object.keys(vars)) s = s.split('{' + k + '}').join(String(vars[k]));
    return s;
  };
}

/* ------------------------------------------------------------------ */
/* Routine logic (approved routine matrix)                             */
/* ------------------------------------------------------------------ */

export const AGE_GROUPS = ['16-24', '25-34', '35-44', '45-54', '55+'];
const MATURE = ['45-54', '55+'];

/**
 * @param {{feel:string, react:string, goals:string[], pores:string, age:string}} a
 */
export function buildRoutine(a) {
  const type = a.feel; // dry | normal | combination | oily
  const sensitive = a.react === 'often';
  const goals = (a.goals || []).filter((g) => g !== 'healthy');
  const has = (g) => goals.includes(g);
  const firm = has('firm');

  // Essence or Serum – never both.
  let treatment;
  let treatmentWhy;
  if (has('tone')) { treatment = 'serum'; treatmentWhy = 'why_serum_tone'; }
  else if (has('clear') || a.pores === 'frequent') { treatment = 'serum'; treatmentWhy = 'why_serum_clear'; }
  else if (has('calm')) { treatment = 'essence'; treatmentWhy = 'why_essence_calm'; }
  else if (has('hydrate')) { treatment = 'essence'; treatmentWhy = 'why_essence_hydrate'; }
  else if (sensitive) { treatment = 'essence'; treatmentWhy = 'why_essence_sensitive'; }
  else if (type === 'oily') { treatment = 'serum'; treatmentWhy = 'why_serum_oily'; }
  else if (type === 'combination') { treatment = 'serum'; treatmentWhy = 'why_serum_combination'; }
  else if (type === 'dry') { treatment = 'essence'; treatmentWhy = 'why_essence_dry'; }
  else { treatment = 'essence'; treatmentWhy = 'why_essence_normal'; }

  const am = [];
  if ((type === 'oily' || type === 'combination') && !sensitive) {
    am.push({ key: 'skinPad', how: 'how_skinPad_am', why: 'why_skinPad_am' });
  }
  am.push({ key: treatment, how: 'how_' + treatment, why: treatmentWhy });
  am.push({ key: 'emulsion', how: 'how_emulsion', why: 'why_emulsion' });
  am.push({ key: 'spf', how: 'spf_how', why: 'spf_why' });

  const pm = [];
  pm.push(sensitive
    ? { key: 'skinPad', how: 'how_skinPad_sensitive', why: 'why_skinPad_sensitive' }
    : { key: 'skinPad', how: 'how_skinPad_pm', why: 'why_skinPad_pm' });
  if (firm) pm.push({ key: 'ampoule', how: 'how_ampoule_' + treatment, why: 'why_ampoule' });
  else pm.push({ key: treatment, how: 'how_' + treatment, why: treatmentWhy });
  const emulsionAmStep = am.findIndex((s) => s.key === 'emulsion') + 1;
  pm.push({ key: 'emulsion', how: 'how_emulsion', why: 'why_emulsion', sameAs: emulsionAmStep });

  // Sheet Mask – extra care, never replaces the Skin Pad.
  let maskFreq = { dry: '2–3', normal: '1–2', combination: '1–2', oily: '1' }[type] || '1–2';
  if (sensitive) maskFreq = '1';
  if (has('hydrate')) maskFreq = '2–3';

  const optionalAmpoule = !firm && MATURE.includes(a.age);

  const complete = [];
  for (const s of [...am, ...pm]) if (s.key !== 'spf' && !complete.includes(s.key)) complete.push(s.key);
  const essentials = [firm ? 'ampoule' : treatment, 'emulsion'];

  const notes = [];
  if (sensitive) notes.push('note_sensitive');
  if (a.pores === 'frequent') notes.push('note_derm');

  // What to expect – clinical results of products in this routine (max 3).
  const ex = [];
  if (firm) ex.push({ t: 'after_1w', v: '+4.28%', k: 'ex_ampoule', fn: 'ampoule', order: 1 });
  if (treatment === 'serum') {
    if (has('tone')) ex.push({ t: 'after_4w', v: '−30.65%', k: 'ex_serum_tone', fn: 'serum', order: 4 });
    else if (has('clear') || a.pores === 'frequent') ex.push({ t: 'after_4w', v: '−51.06%', k: 'ex_serum_clear', fn: 'serum', order: 4 });
    else ex.push({ t: 'after_4w', v: '−41.63%', k: 'ex_serum_oil', fn: 'serum', order: 4 });
  }
  if (treatment === 'essence' && (has('calm') || sensitive)) {
    ex.push({ t: 'after_once', v: '−13.75%', k: 'ex_essence', fn: 'essence', order: 0 });
  }
  ex.push({ t: 'after_2w', v: '+62.72%', k: 'ex_emulsion', fn: 'emulsion', order: 2 });
  if (type === 'oily' || type === 'combination') ex.push({ t: 'after_4w', v: '−29.50%', k: 'ex_skinPad_sebum', fn: 'skinPad', order: 4 });
  else ex.push({ t: 'after_4w', v: '−34.91%', k: 'ex_skinPad_cells', fn: 'skinPad', order: 4 });
  const expect = ex.slice(0, 3).sort((x, y) => x.order - y.order);

  return {
    type, sensitive, goals: (a.goals || []).slice(0, 2), am, pm, maskFreq,
    optionalAmpoule, complete, essentials, notes, expect, treatment, firm
  };
}

/* ------------------------------------------------------------------ */
/* Money                                                               */
/* ------------------------------------------------------------------ */

export function formatMoney(cents, format) {
  const fmt = format || '€{{amount_with_comma_separator}}';
  const num = (n, decimals, thousands, dec) => {
    const fixed = (n / 100).toFixed(decimals);
    const [i, d] = fixed.split('.');
    const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return d ? int + dec + d : int;
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount': return num(cents, 2, ',', '.');
      case 'amount_no_decimals': return num(cents, 0, ',', '.');
      case 'amount_with_comma_separator': return num(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return num(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return num(cents, 2, '’', '.');
      case 'amount_with_period_and_space_separator': return num(cents, 2, ' ', '.');
      case 'amount_with_space_separator': return num(cents, 2, ' ', ',');
      default: return num(cents, 2, ',', '.');
    }
  }).replace(/<[^>]*>/g, '');
}

/* ------------------------------------------------------------------ */
/* Image analysis (runs only in this browser)                          */
/* ------------------------------------------------------------------ */

function srgbToLab(r, g, b) {
  const lin = (c) => { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  const R = lin(r), G = lin(g), B = lin(b);
  let x = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
  let y = (R * 0.2126 + G * 0.7152 + B * 0.0722);
  let z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  x = f(x); y = f(y); z = f(z);
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
}

function median(arr) {
  if (!arr.length) return 0;
  const s = Float64Array.from(arr).sort();
  return s[Math.floor(s.length / 2)];
}

/** Sample a rectangular region into a fixed 32x32 grid of Lab values. */
function sampleRegion(img, cx, cy, rw, rh) {
  const { data, width, height } = img;
  const N = 32;
  const out = [];
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const x = Math.round(cx - rw / 2 + (i + 0.5) * rw / N);
      const y = Math.round(cy - rh / 2 + (j + 0.5) * rh / N);
      if (x < 0 || y < 0 || x >= width || y >= height) { out.push(null); continue; }
      const o = (y * width + x) * 4;
      out.push(srgbToLab(data[o], data[o + 1], data[o + 2]));
    }
  }
  return out;
}

/**
 * Measures visible shine, redness and tone evenness.
 * img: ImageData of the captured frame; lm: 478 normalized face landmarks.
 * NOTE: thresholds are first estimates and should be tuned with real photos.
 */
export function analyzeSkin(img, lm) {
  const W = img.width, H = img.height;
  const P = (i) => ({ x: lm[i].x * W, y: lm[i].y * H });
  const faceW = Math.hypot(P(454).x - P(234).x, P(454).y - P(234).y);
  const faceH = Math.hypot(P(152).x - P(10).x, P(152).y - P(10).y);
  const box = (i, wf, hf, dy = 0) => sampleRegion(img, P(i).x, P(i).y + dy * faceH, faceW * wf, faceH * hf);

  const regions = {
    forehead: box(151, 0.34, 0.10, -0.02),
    nose: box(5, 0.10, 0.14, 0.02),
    chin: box(200, 0.18, 0.07),
    cheekL: box(50, 0.15, 0.13, 0.02),
    cheekR: box(280, 0.15, 0.13, 0.02)
  };

  const all = Object.values(regions).flat().filter((p) => p && p[0] > 20);
  const medL = median(all.map((p) => p[0]));
  const isShine = (p) => p && p[0] > Math.max(78, medL + 14) && Math.hypot(p[1], p[2]) < 16;
  const medA = median(all.filter((p) => !isShine(p)).map((p) => p[1]));

  const frac = (pts, fn) => { const v = pts.filter(Boolean); return v.length ? v.filter(fn).length / v.length : 0; };

  const tZone = [...regions.forehead, ...regions.nose, ...regions.chin];
  const uZone = [...regions.cheekL, ...regions.cheekR];
  const shineT = frac(tZone, isShine);
  const shineU = frac(uZone, isShine);
  const redness = frac(uZone.filter((p) => p && !isShine(p)), (p) => p[0] > 20 && p[1] > medA + 7);

  // Tone: compare each point with its surroundings (local contrast), so that
  // face shape and light falling from one side are not mistaken for spots.
  const localDiff = (grid) => {
    const N = 32, R = 4, out = [];
    const L = (i, j) => { const p = grid[j * N + i]; return p && p[0] > 20 && !isShine(p) ? p[0] : null; };
    for (let j = R; j < N - R; j++) for (let i = R; i < N - R; i++) {
      const c = L(i, j); if (c === null) continue;
      let s = 0, n = 0, sc = 0, nc = 0;
      for (let dj = -R; dj <= R; dj++) for (let di = -R; di <= R; di++) {
        const v = L(i + di, j + dj); if (v === null) continue;
        s += v; n++;
        if (Math.abs(di) <= 1 && Math.abs(dj) <= 1) { sc += v; nc++; }
      }
      if (n > 30 && nc > 4) out.push(sc / nc - s / n);
    }
    return out;
  };
  const toneVals = [...localDiff(regions.cheekL), ...localDiff(regions.cheekR), ...localDiff(regions.forehead)];
  const spots = toneVals.length ? toneVals.filter((v) => v < -5).length / toneVals.length : 0;
  const toneStd = Math.sqrt(toneVals.reduce((s, v) => s + v * v, 0) / (toneVals.length || 1));

  // Lighting check: overall brightness and left/right balance.
  const lMed = (g) => median(g.filter((p) => p && p[0] > 20).map((p) => p[0]));
  const balance = Math.abs(lMed(regions.cheekL) - lMed(regions.cheekR));
  const lightOk = medL > 35 && medL < 88 && balance < 20;

  return { shineT, shineU, redness, spots, toneStd, medL, balance, lightOk, faceW: faceW / W };
}

export function levelsFromMetrics(m) {
  const shine = m.shineT >= 0.05 && m.shineU >= 0.03 ? 2 : m.shineT >= 0.03 ? 1 : 0;
  const red = m.redness >= 0.22 ? 2 : m.redness >= 0.12 ? 1 : 0;
  const tone = (m.spots >= 0.08 || m.toneStd >= 3.5) ? 2 : (m.spots >= 0.04 || m.toneStd >= 2.6) ? 1 : 0;
  return { shine, red, tone, lightOk: m.lightOk };
}

export function prefillFromLevels(lv) {
  const pre = { feel: null, goals: [], pores: null };
  if (lv.shine === 2) pre.feel = 'oily';
  else if (lv.shine === 1) pre.feel = 'combination';
  if (lv.red >= 1) pre.goals.push('calm');
  if (lv.tone >= 1) pre.goals.push('tone');
  pre.goals = pre.goals.slice(0, 2);
  return pre;
}

/* ------------------------------------------------------------------ */
/* App                                                                 */
/* ------------------------------------------------------------------ */

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const ICON = {
  lock: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
  check: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>',
  sun: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>',
  drop: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M12 3c3 4 6 7.5 6 11a6 6 0 0 1-12 0c0-3.5 3-7 6-11z"/></svg>',
  hair: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><circle cx="12" cy="10" r="5"/><path d="M5 21c1.5-3.5 4-5 7-5s5.5 1.5 7 5"/></svg>',
  face: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M9 10h.01M15 10h.01M9 15h6"/></svg>',
  close: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
  dot: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="8"/></svg>'
};

class SkinCheck {
  constructor(root, config) {
    this.root = root;
    this.cfg = config;
    this.lang = (config.locale || 'en').slice(0, 2).toLowerCase();
    this.t = makeT(this.lang);
    this.products = config.products || {};
    this.engine = null;
    this.enginePromise = null;
    this.stream = null;
    this.raf = null;
    this.answers = { feel: null, react: null, goals: [], pores: null, age: null };
    this.prefill = { feel: null, goals: [], pores: null };
    this.levels = null;
    this.usedPhoto = false;
    this.qIndex = 0;
    this.photoAvailable = config.photoEnabled !== false && !!(config.engine && config.engine.wasm && config.engine.model)
      && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    this.debug = /[?&]bsc_debug=1/.test(location.search);
    window.addEventListener('pagehide', () => { this.stopCamera(); if (this.wasmUrl) URL.revokeObjectURL(this.wasmUrl); });
    this.show('start');
  }

  /* ---------- rendering helpers ---------- */

  show(screen, opts = {}) {
    if (screen !== 'camera') this.stopCamera();
    this.screen = screen;
    const html = this['view_' + screen](opts);
    this.root.innerHTML = html;
    this.root.dataset.screen = screen;
    this.bind();
    const focus = this.root.querySelector('[data-focus]') || this.root.querySelector('h1, h2');
    if (focus) { focus.setAttribute('tabindex', '-1'); focus.focus({ preventScroll: true }); }
    if (!opts.keepScroll) {
      const top = this.root.getBoundingClientRect().top + window.scrollY - 120;
      if (window.scrollY > top) window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
    if (screen === 'camera') this.startCamera();
  }

  bind() {
    this.root.querySelectorAll('[data-go]').forEach((el) => el.addEventListener('click', (e) => {
      e.preventDefault();
      const go = el.dataset.go;
      if (go === 'photo') { this.usedPhoto = true; this.show('privacy'); }
      else if (go === 'questions') { this.usedPhoto = false; this.prefill = { feel: null, goals: [], pores: null }; this.levels = null; this.qIndex = 0; this.show('question'); }
      else this.show(go);
    }));
    const consent = this.root.querySelector('#bsc-consent');
    if (consent) {
      const btn = this.root.querySelector('[data-consent-continue]');
      const sync = () => { btn.disabled = !consent.checked; };
      consent.addEventListener('change', sync); sync();
      btn.addEventListener('click', () => { if (consent.checked) { this.loadEngine(); this.show('tips'); } });
    }
    const open = this.root.querySelector('[data-open-camera]');
    if (open) open.addEventListener('click', () => this.show('camera'));
    const shutter = this.root.querySelector('[data-shutter]');
    if (shutter) shutter.addEventListener('click', () => this.capture());
    this.root.querySelectorAll('[data-q-input]').forEach((inp) => inp.addEventListener('change', () => this.onAnswer(inp)));
    const next = this.root.querySelector('[data-q-next]');
    if (next) next.addEventListener('click', () => this.nextQuestion());
    const prev = this.root.querySelector('[data-q-prev]');
    if (prev) prev.addEventListener('click', () => this.prevQuestion());
    this.root.querySelectorAll('button[data-tab]').forEach((b) => b.addEventListener('click', () => {
      this.root.querySelector('.bsc-result').dataset.tab = b.dataset.tab;
      this.root.querySelectorAll('button[data-tab]').forEach((x) => x.setAttribute('aria-selected', String(x === b)));
    }));
    this.root.querySelectorAll('[data-add]').forEach((b) => b.addEventListener('click', () => this.addToCart(b)));
  }

  progress(n, label) {
    const bars = [1, 2, 3, 4].map((i) => `<span class="bsc-progress__bar${i <= n ? ' is-on' : ''}"></span>`).join('');
    return `<div class="bsc-progress"><div class="bsc-progress__bars">${bars}</div><p class="bsc-progress__label">${esc(this.t('progress', { n, label }))}</p></div>`;
  }

  /* ---------- screens ---------- */

  view_start() {
    const t = this.t;
    const steps = [1, 2, 3].map((n) => `
      <li class="bsc-card bsc-step">
        <span class="bsc-num">${n}</span>
        <p class="bsc-step__t">${esc(t('step' + n + '_t'))}</p>
        <p class="bsc-step__d">${esc(t('step' + n + '_d'))}</p>
      </li>`).join('');
    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    return `
      <div class="bsc-screen bsc-start">
        <div class="bsc-head bsc-center">
          <p class="bsc-eyebrow">${esc(t('eyebrow'))}</p>
          <h1 class="bsc-h1" data-focus>${esc(t('start_title'))}</h1>
          <p class="bsc-lead">${esc(t('start_lead'))}</p>
        </div>
        <ol class="bsc-steps">${steps}</ol>
        <div class="bsc-actions bsc-center">
          ${this.photoAvailable ? `<button type="button" class="bsc-btn bsc-btn--primary" data-go="photo">${esc(t('start_cta'))}</button>` : `<button type="button" class="bsc-btn bsc-btn--primary" data-go="questions">${esc(t('start_cta'))}</button>`}
          ${this.photoAvailable ? `<button type="button" class="bsc-link" data-go="questions">${esc(t('skip_photo'))}</button>` : ''}
          ${this.photoAvailable ? `<p class="bsc-note">${ICON.lock} ${esc(t('photo_stays'))}</p>` : ''}
          ${this.photoAvailable && isDesktop ? `<p class="bsc-note bsc-note--muted">${esc(t('desktop_hint'))}</p>` : ''}
        </div>
      </div>`;
  }

  view_privacy() {
    const t = this.t;
    const pts = [1, 2, 3].map((n) => `<li>${ICON.check}<span>${esc(t('privacy_' + n))}</span></li>`).join('');
    return `
      <div class="bsc-screen bsc-narrow">
        ${this.progress(1, t('p_privacy'))}
        <h2 class="bsc-h2" data-focus>${esc(t('privacy_title'))}</h2>
        <p class="bsc-lead">${esc(t('privacy_lead'))}</p>
        <ul class="bsc-checks">${pts}</ul>
        <label class="bsc-card bsc-consent" for="bsc-consent">
          <input type="checkbox" id="bsc-consent">
          <span>${esc(t('consent'))} <a href="${esc(this.cfg.privacyUrl)}" target="_blank" rel="noopener">${esc(t('privacy_link'))}</a></span>
        </label>
        <div class="bsc-actions">
          <button type="button" class="bsc-btn bsc-btn--primary bsc-btn--block" data-consent-continue disabled>${esc(t('continue'))}</button>
          <button type="button" class="bsc-link" data-go="start">${esc(t('back'))}</button>
        </div>
      </div>`;
  }

  view_tips() {
    const t = this.t;
    const icons = [ICON.sun, ICON.drop, ICON.hair, ICON.face];
    const tips = [1, 2, 3, 4].map((n) => `
      <li class="bsc-card bsc-tip">
        <span class="bsc-tip__icon">${icons[n - 1]}</span>
        <p class="bsc-tip__t">${esc(t('tip' + n + '_t'))}</p>
        <p class="bsc-tip__d">${esc(t('tip' + n + '_d'))}</p>
      </li>`).join('');
    return `
      <div class="bsc-screen bsc-narrow">
        ${this.progress(2, t('p_ready'))}
        <h2 class="bsc-h2" data-focus>${esc(t('tips_title'))}</h2>
        <p class="bsc-lead">${esc(t('tips_lead'))}</p>
        <ul class="bsc-tips">${tips}</ul>
        <div class="bsc-actions">
          <button type="button" class="bsc-btn bsc-btn--primary bsc-btn--block" data-open-camera>${esc(t('open_camera'))}</button>
          <p class="bsc-note bsc-note--muted">${esc(t('camera_note'))}</p>
        </div>
      </div>`;
  }

  view_camera() {
    const t = this.t;
    return `
      <div class="bsc-camera">
        <div class="bsc-camera__top">
          <button type="button" class="bsc-icon-btn" data-go="tips" aria-label="${esc(t('close'))}">${ICON.close}</button>
          <p class="bsc-camera__step">${esc(t('progress', { n: 3, label: t('p_photo') }))}</p>
          <span class="bsc-icon-btn" aria-hidden="true"></span>
        </div>
        <div class="bsc-chips" data-chips><span class="bsc-chip">${ICON.dot}${esc(t('preparing'))}</span></div>
        <div class="bsc-camera__stage">
          <video class="bsc-camera__video" playsinline muted autoplay></video>
          <svg class="bsc-camera__oval" viewBox="0 0 250 330" aria-hidden="true"><ellipse cx="125" cy="165" rx="118" ry="158" fill="none" stroke="#fff" stroke-width="2" stroke-dasharray="6 7"/></svg>
        </div>
        <p class="bsc-camera__hint">${esc(t('camera_place'))}</p>
        <div class="bsc-camera__controls">
          <button type="button" class="bsc-shutter" data-shutter aria-label="${esc(t('take_photo'))}" disabled></button>
        </div>
        <p class="bsc-camera__footer">${ICON.lock} ${esc(t('camera_footer'))}</p>
      </div>`;
  }

  view_analysing() {
    return `<div class="bsc-screen bsc-center bsc-analysing"><span class="bsc-spinner" aria-hidden="true"></span><p class="bsc-lead" data-focus>${esc(this.t('analysing'))}</p></div>`;
  }

  view_error({ key, retry }) {
    const t = this.t;
    return `
      <div class="bsc-screen bsc-narrow bsc-center">
        <p class="bsc-lead" data-focus>${esc(t(key))}</p>
        <div class="bsc-actions">
          ${retry ? `<button type="button" class="bsc-btn bsc-btn--primary bsc-btn--block" data-go="camera">${esc(t('try_again'))}</button>` : ''}
          <button type="button" class="${retry ? 'bsc-link' : 'bsc-btn bsc-btn--primary bsc-btn--block'}" data-go="questions">${esc(t('questions_only'))}</button>
        </div>
      </div>`;
  }

  view_seen() {
    const t = this.t;
    const lv = this.levels;
    const row = (label, value, n) => `
      <li class="bsc-seen__row">
        <span class="bsc-seen__label">${esc(label)}</span>
        <span class="bsc-seen__meter" aria-hidden="true">${[0, 1, 2].map((i) => `<i class="${i <= n ? 'is-on' : ''}"></i>`).join('')}</span>
        <span class="bsc-seen__value">${esc(value)}</span>
      </li>`;
    return `
      <div class="bsc-screen bsc-narrow">
        ${this.progress(4, t('p_questions'))}
        <h2 class="bsc-h2" data-focus>${esc(t('seen_title'))}</h2>
        <p class="bsc-lead">${esc(t('seen_lead'))}</p>
        <ul class="bsc-seen">
          ${row(t('seen_shine'), t('lvl_shine_' + lv.shine), lv.shine)}
          ${row(t('seen_redness'), t('lvl_red_' + lv.red), lv.red)}
          ${row(t('seen_tone'), t('lvl_tone_' + lv.tone), lv.tone)}
        </ul>
        ${lv.lightOk ? '' : `<p class="bsc-note">${esc(t('seen_light'))}</p>`}
        <div class="bsc-actions">
          <button type="button" class="bsc-btn bsc-btn--primary bsc-btn--block" data-go="question">${esc(t('seen_cta'))}</button>
        </div>
      </div>`;
  }

  questions() {
    return [
      { id: 'feel', q: 'q1', opts: ['dry', 'normal', 'combination', 'oily'] },
      { id: 'react', q: 'q2', opts: ['calm', 'some', 'often'] },
      { id: 'goals', q: 'q3', opts: ['firm', 'tone', 'clear', 'calm', 'hydrate', 'healthy'], multi: true },
      { id: 'pores', q: 'q4', opts: ['barely', 'visible', 'occasional', 'frequent'] },
      { id: 'age', q: 'q5', opts: AGE_GROUPS, raw: true }
    ];
  }

  view_question() {
    const t = this.t;
    const q = this.questions()[this.qIndex];
    // Apply scan pre-selection once, if the visitor has not answered yet.
    if (q.id === 'feel' && !this.answers.feel && this.prefill.feel) this.answers.feel = this.prefill.feel;
    if (q.id === 'goals' && !this.answers.goals.length && this.prefill.goals.length) this.answers.goals = [...this.prefill.goals];
    const val = this.answers[q.id];
    const fromScan = (o) => (q.id === 'feel' && this.prefill.feel === o) || (q.id === 'goals' && this.prefill.goals.includes(o));
    const opts = q.opts.map((o) => {
      const checked = q.multi ? val.includes(o) : val === o;
      const label = q.raw ? o : t(q.q + '_' + o);
      return `
        <label class="bsc-option${checked ? ' is-checked' : ''}">
          <input type="${q.multi ? 'checkbox' : 'radio'}" name="bsc-${q.id}" value="${esc(o)}" data-q-input ${checked ? 'checked' : ''}>
          <span class="bsc-option__text">${esc(label)}</span>
          ${fromScan(o) ? `<span class="bsc-option__tag">${esc(t('from_scan'))}</span>` : ''}
        </label>`;
    }).join('');
    const answered = q.multi ? val.length > 0 : !!val;
    const last = this.qIndex === 4;
    return `
      <div class="bsc-screen bsc-narrow">
        ${this.usedPhoto ? this.progress(4, t('p_questions')) : ''}
        <fieldset class="bsc-question">
          <legend>
            <span class="bsc-qcount">${esc(t('q_counter', { n: this.qIndex + 1 }))}</span>
            <span class="bsc-h2 bsc-q" data-focus>${esc(t(q.q))}</span>
            ${q.multi ? `<span class="bsc-note bsc-note--muted">${esc(t('choose_up_to_2'))}</span>` : ''}
          </legend>
          <div class="bsc-options">${opts}</div>
        </fieldset>
        <div class="bsc-actions">
          <button type="button" class="bsc-btn bsc-btn--primary bsc-btn--block" data-q-next ${answered ? '' : 'disabled'}>${esc(last ? t('show_routine') : t('continue'))}</button>
          <button type="button" class="bsc-link" data-q-prev>${esc(t('back'))}</button>
        </div>
      </div>`;
  }

  onAnswer(inp) {
    const q = this.questions()[this.qIndex];
    if (q.multi) {
      let g = [...this.answers.goals];
      if (inp.checked) {
        if (inp.value === 'healthy') g = ['healthy'];
        else { g = g.filter((x) => x !== 'healthy'); g.push(inp.value); if (g.length > 2) g = g.slice(-2); }
      } else g = g.filter((x) => x !== inp.value);
      this.answers.goals = g;
      this.root.querySelectorAll('[data-q-input]').forEach((el) => {
        el.checked = g.includes(el.value);
        el.closest('.bsc-option').classList.toggle('is-checked', el.checked);
      });
    } else {
      this.answers[q.id] = inp.value;
      this.root.querySelectorAll('[data-q-input]').forEach((el) => el.closest('.bsc-option').classList.toggle('is-checked', el.checked));
    }
    const next = this.root.querySelector('[data-q-next]');
    const v = this.answers[q.id];
    next.disabled = q.multi ? v.length === 0 : !v;
  }

  nextQuestion() {
    if (this.qIndex < 4) { this.qIndex++; this.show('question'); }
    else { this.routine = buildRoutine(this.answers); this.show('result'); }
  }

  prevQuestion() {
    if (this.qIndex > 0) { this.qIndex--; this.show('question'); }
    else this.show(this.usedPhoto && this.levels ? 'seen' : 'start');
  }

  /* ---------- result ---------- */

  product(key) { return this.products[key] || null; }

  card(step, n, kind) {
    const t = this.t;
    const num = `<span class="bsc-num">${esc(n)}</span>`;
    if (step.key === 'spf') {
      return `
        <li class="bsc-card bsc-prod">
          ${num}
          <div class="bsc-prod__img bsc-prod__img--icon">${ICON.sun}</div>
          <div class="bsc-prod__body">
            <h3 class="bsc-prod__name">${esc(t('spf_title'))}</h3>
            <p class="bsc-prod__desc">${esc(t('spf_desc'))}</p>
            <p class="bsc-prod__how">${esc(t('spf_how'))}</p>
            <p class="bsc-prod__why"><strong>${esc(t('why'))}</strong> ${esc(t('spf_why'))}</p>
            <p class="bsc-prod__meta">${esc(t('spf_note'))}</p>
          </div>
        </li>`;
    }
    const p = this.product(step.key);
    const name = p ? p.title : step.key;
    const img = p && p.image ? `<img src="${esc(p.image)}" alt="${esc(name)}" loading="lazy" width="300" height="300">` : '';
    const how = kind === 'mask' ? t('how_sheetMask') : t(step.how);
    let foot;
    if (step.sameAs) {
      foot = `<p class="bsc-prod__same">${ICON.check}${esc(t('same_bottle', { n: step.sameAs }))}</p>`;
    } else if (p) {
      foot = `
        <p class="bsc-prod__price">${esc(formatMoney(p.price, this.cfg.moneyFormat))}</p>
        ${p.available ? '' : `<p class="bsc-prod__meta">${esc(t('unavailable'))}</p>`}
        <a class="bsc-btn bsc-btn--pill" href="${esc(p.url)}">${esc(t('shop'))}</a>`;
    } else foot = '';
    return `
      <li class="bsc-card bsc-prod">
        ${num}
        <div class="bsc-prod__img">${img}</div>
        <div class="bsc-prod__body">
          <h3 class="bsc-prod__name">${esc(name)}</h3>
          <p class="bsc-prod__desc">${esc(t('desc_' + step.key))}</p>
          <p class="bsc-prod__how">${esc(how)}</p>
          ${step.why ? `<p class="bsc-prod__why"><strong>${esc(t('why'))}</strong> ${esc(t(step.why))}</p>` : ''}
          <div class="bsc-prod__foot">${foot}</div>
        </div>
      </li>`;
  }

  sum(keys) {
    let total = 0, count = 0;
    for (const k of keys) { const p = this.product(k); if (p && p.available) { total += p.price; count++; } }
    return { total, count };
  }

  view_result() {
    const t = this.t;
    const r = this.routine;
    const typeLabel = t('type_' + r.type) + (r.sensitive ? t('sensitive_suffix') : '');
    const goalLabel = (r.goals.length ? r.goals : ['healthy']).map((g) => t('goal_' + g)).join(' · ');
    const amCards = r.am.map((s, i) => this.card(s, i + 1)).join('');
    const pmCards = r.pm.map((s, i) => this.card(s, i + 1)).join('');
    const maskCard = this.card({ key: 'sheetMask', why: null }, '+', 'mask');
    const complete = this.sum(r.complete);
    const essentials = this.sum(r.essentials);
    const firstEss = r.essentials.map((k) => (this.product(k) ? this.product(k).title : k)).join(' + ');
    const completeNames = r.complete.map((k) => (this.product(k) ? this.product(k).title : k)).join(', ');
    const fnKeys = [...new Set(r.expect.map((e) => e.fn))];
    const sup = (fn) => fnKeys.indexOf(fn) + 1;
    const optional = r.optionalAmpoule && this.product('ampoule') ? `
      <section class="bsc-block">
        <h2 class="bsc-h2 bsc-center">${esc(t('optional_title'))}</h2>
        <p class="bsc-lead bsc-center">${esc(t('optional_lead'))}</p>
        <ul class="bsc-cards bsc-cards--single">${this.card({ key: 'ampoule', how: 'how_ampoule_' + r.treatment, why: null }, '+')}</ul>
      </section>` : '';
    const money = (c) => esc(formatMoney(c, this.cfg.moneyFormat));
    return `
      <div class="bsc-result" data-tab="am">
        <div class="bsc-head bsc-center">
          <p class="bsc-eyebrow">${esc(t('result_eyebrow'))}</p>
          <h1 class="bsc-h1" data-focus>${esc(t('result_title'))}</h1>
          <p class="bsc-profile"><span>${esc(t('label_type'))}</span> ${esc(typeLabel)}<span class="bsc-dot" aria-hidden="true">·</span><span>${esc(t('label_goal'))}</span> ${esc(goalLabel)}</p>
          <p class="bsc-lead">${esc(t('result_lead'))}</p>
          ${r.notes.map((k) => `<p class="bsc-note">${esc(t(k))}</p>`).join('')}
        </div>

        <div class="bsc-tabs" role="tablist">
          <button type="button" role="tab" aria-selected="true" data-tab="am">${esc(t('morning'))}</button>
          <button type="button" role="tab" aria-selected="false" data-tab="pm">${esc(t('evening'))}</button>
        </div>

        <section class="bsc-block bsc-am">
          <h2 class="bsc-h2 bsc-center bsc-daytitle">${esc(t('morning_title'))}</h2>
          <ul class="bsc-cards">${amCards}</ul>
        </section>
        <section class="bsc-block bsc-pm">
          <h2 class="bsc-h2 bsc-center bsc-daytitle">${esc(t('evening_title'))}</h2>
          <ul class="bsc-cards">${pmCards}</ul>
        </section>

        <section class="bsc-block bsc-extra">
          <h2 class="bsc-h2 bsc-center">${esc(t('extra_title'))}</h2>
          <p class="bsc-lead bsc-center">${esc(t('extra_freq', { f: r.maskFreq }))}</p>
          <ul class="bsc-cards bsc-cards--single">${maskCard}</ul>
        </section>

        ${optional}

        <section class="bsc-block">
          <h2 class="bsc-h2 bsc-center bsc-bold">${esc(t('expect_title'))}</h2>
          <p class="bsc-lead bsc-center">${esc(t('expect_lead'))}</p>
          <ol class="bsc-expect">
            ${r.expect.map((e) => `<li><p class="bsc-expect__when">${esc(t(e.t))}</p><p class="bsc-expect__value">${esc(this.lang === 'en' ? e.v : e.v.replace('.', ','))}</p><p class="bsc-expect__what">${esc(t(e.k))}<sup>${sup(e.fn)}</sup></p></li>`).join('')}
          </ol>
          <p class="bsc-footnote">${fnKeys.map((fn, i) => `${i + 1} ${esc(t('fn_' + fn))}`).join(' ')} ${esc(t('fn_all'))}</p>
        </section>

        <section class="bsc-block">
          <h2 class="bsc-h2 bsc-center">${esc(t('choose_title'))}</h2>
          <div class="bsc-choose">
            <div class="bsc-card bsc-option-card">
              <p class="bsc-eyebrow">${esc(t('recommended'))}</p>
              <h3 class="bsc-option-card__t">${esc(t('complete_title'))}</h3>
              <p class="bsc-prod__desc">${esc(completeNames)}</p>
              <div class="bsc-option-card__foot">
                <span class="bsc-option-card__price">${money(complete.total)}</span>
                <button type="button" class="bsc-btn bsc-btn--primary" data-add="complete">${esc(t('add_routine'))}</button>
              </div>
            </div>
            <div class="bsc-card bsc-option-card">
              <p class="bsc-eyebrow">${esc(t('start_smaller'))}</p>
              <h3 class="bsc-option-card__t">${esc(t('essentials_title'))}</h3>
              <p class="bsc-prod__desc">${esc(firstEss)} — ${esc(t('essentials_lead'))}</p>
              <div class="bsc-option-card__foot">
                <span class="bsc-option-card__price">${money(essentials.total)}</span>
                <button type="button" class="bsc-btn bsc-btn--pill" data-add="essentials">${esc(t('add_essentials'))}</button>
              </div>
            </div>
          </div>
          <p class="bsc-error" data-add-error hidden>${esc(t('add_error'))}</p>
          <div class="bsc-center"><button type="button" class="bsc-link" data-go="start">${esc(t('retake'))}</button></div>
        </section>

        <div class="bsc-bar">
          <div class="bsc-bar__sum"><span class="bsc-bar__price">${money(complete.total)}</span><span class="bsc-bar__count">${esc(t('products_count', { n: complete.count }))}</span></div>
          <button type="button" class="bsc-btn bsc-btn--primary" data-add="complete">${esc(t('add_routine'))}</button>
        </div>
      </div>`;
  }

  async addToCart(btn) {
    const keys = btn.dataset.add === 'essentials' ? this.routine.essentials : this.routine.complete;
    const items = keys.map((k) => this.product(k)).filter((p) => p && p.available && p.variantId).map((p) => ({ id: p.variantId, quantity: 1 }));
    const err = this.root.querySelector('[data-add-error]');
    if (!items.length) { err.hidden = false; return; }
    const label = btn.textContent;
    btn.disabled = true; btn.textContent = this.t('adding');
    try {
      const res = await fetch(this.cfg.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error('cart ' + res.status);
      window.location.href = this.cfg.cartUrl;
    } catch (e) {
      btn.disabled = false; btn.textContent = label; err.hidden = false;
    }
  }

  /* ---------- engine + camera ---------- */

  loadEngine() {
    if (this.enginePromise) return this.enginePromise;
    const e = this.cfg.engine;
    this.enginePromise = (async () => {
      // Download each engine file once. The wasm is wrapped in a local blob with the
      // right type, so the browser compiles it directly and retries reuse it.
      const [vision, wasmBuf, modelBuf] = await Promise.all([
        import(e.bundle),
        fetch(e.wasm).then((r) => { if (!r.ok) throw new Error('wasm'); return r.arrayBuffer(); }),
        fetch(e.model).then((r) => { if (!r.ok) throw new Error('model'); return r.arrayBuffer(); })
      ]);
      this.wasmUrl = URL.createObjectURL(new Blob([wasmBuf], { type: 'application/wasm' }));
      const fileset = { wasmLoaderPath: e.loader, wasmBinaryPath: this.wasmUrl };
      const opts = (delegate) => ({ baseOptions: { modelAssetBuffer: new Uint8Array(modelBuf), delegate }, runningMode: 'VIDEO', numFaces: 1 });
      try { this.engine = await vision.FaceLandmarker.createFromOptions(fileset, opts('GPU')); }
      catch (err) { this.engine = await vision.FaceLandmarker.createFromOptions(fileset, opts('CPU')); }
      return this.engine;
    })();
    this.enginePromise.catch(() => { this.enginePromise = null; });
    return this.enginePromise;
  }

  async startCamera() {
    const t = this.t;
    const video = this.root.querySelector('video');
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } }, audio: false
      });
    } catch (err) {
      this.show('error', { key: 'camera_error' });
      return;
    }
    if (this.screen !== 'camera') { this.stopCamera(); return; }
    video.srcObject = this.stream;
    await video.play().catch(() => {});
    try { await this.loadEngine(); }
    catch (err) { this.show('error', { key: 'engine_error' }); return; }
    if (this.screen !== 'camera') return;
    this.root.querySelector('[data-shutter]').disabled = false;
    this.guide(video);
  }

  guide(video) {
    const chips = this.root.querySelector('[data-chips]');
    const small = document.createElement('canvas');
    small.width = 48; small.height = 48;
    const sctx = small.getContext('2d', { willReadFrequently: true });
    let last = 0, prevNose = null, stillSince = 0;
    const tick = (now) => {
      if (this.screen !== 'camera' || !this.stream) return;
      this.raf = requestAnimationFrame(tick);
      if (now - last < 160 || video.readyState < 2) return;
      last = now;
      let res;
      try { res = this.engine.detectForVideo(video, now); } catch (e) { return; }
      const lm = res && res.faceLandmarks && res.faceLandmarks[0];
      if (!lm) { chips.innerHTML = `<span class="bsc-chip">${ICON.dot}${esc(this.t('chip_noface'))}</span>`; prevNose = null; return; }
      const xs = lm.map((p) => p.x), ys = lm.map((p) => p.y);
      const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
      const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2, w = maxX - minX;
      const centred = Math.abs(cx - 0.5) < 0.12 && Math.abs(cy - 0.5) < 0.15 && w > 0.28 && w < 0.85;
      sctx.drawImage(video, minX * video.videoWidth, minY * video.videoHeight, w * video.videoWidth, (maxY - minY) * video.videoHeight, 0, 0, 48, 48);
      const d = sctx.getImageData(0, 0, 48, 48).data;
      let s = 0; for (let i = 0; i < d.length; i += 4) s += 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
      const bright = s / (d.length / 4);
      const light = bright > 80 && bright < 225;
      const nose = lm[4];
      const moved = prevNose ? Math.hypot(nose.x - prevNose.x, nose.y - prevNose.y) : 1;
      prevNose = nose;
      if (moved > 0.008) stillSince = now;
      const still = now - stillSince > 500;
      const chip = (ok, key) => `<span class="bsc-chip${ok ? ' is-ok' : ''}">${ok ? ICON.check : ICON.dot}${esc(this.t(key))}</span>`;
      chips.innerHTML = chip(light, 'chip_light') + chip(centred, 'chip_centre') + chip(still, 'chip_still');
    };
    this.raf = requestAnimationFrame(tick);
  }

  stopCamera() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
    if (this.stream) { this.stream.getTracks().forEach((tr) => tr.stop()); this.stream = null; }
    const v = this.root && this.root.querySelector('video');
    if (v) v.srcObject = null;
  }

  async capture() {
    const video = this.root.querySelector('video');
    if (!video || !this.engine || video.readyState < 2) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0);
    this.stopCamera(); // camera off immediately after the photo
    this.show('analysing');
    await new Promise((r) => setTimeout(r, 50));
    let metrics = null;
    try {
      const res = this.engine.detectForVideo(canvas, performance.now());
      const lm = res && res.faceLandmarks && res.faceLandmarks[0];
      if (lm) metrics = analyzeSkin(ctx.getImageData(0, 0, canvas.width, canvas.height), lm);
    } catch (e) { metrics = null; }
    // Wipe the image: nothing of the photo is kept.
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = 0; canvas.height = 0;
    if (!metrics) { this.show('error', { key: 'noface_error', retry: true }); return; }
    if (this.debug) console.info('[Bright skin check] metrics', metrics);
    this.levels = levelsFromMetrics(metrics);
    this.prefill = prefillFromLevels(this.levels);
    this.answers = { feel: null, react: null, goals: [], pores: null, age: null };
    this.qIndex = 0;
    await new Promise((r) => setTimeout(r, 600));
    this.show('seen');
  }
}

/* ------------------------------------------------------------------ */
/* Boot                                                                */
/* ------------------------------------------------------------------ */

if (typeof document !== 'undefined') {
  const boot = () => {
    document.querySelectorAll('[data-bright-skin-check]').forEach((root) => {
      if (root.__bsc) return;
      const cfgEl = root.closest('.shopify-section, .bsc-section')?.parentElement?.querySelector('[data-bright-skin-check-config]')
        || document.querySelector('[data-bright-skin-check-config]');
      let cfg = {};
      try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { cfg = {}; }
      root.__bsc = new SkinCheck(root, cfg);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}
